import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Translate, I18n } from 'react-redux-i18n';
import NavigationStateManager from '../../../helpers/NavigationStateManager';

import * as templateBuilderActions from '../../../modules/template-builder-ex';
import * as generalActions from '../../../modules/general';
import { clearRecordMessages } from '../../stripe/reducer';
import { organizationActions } from '../store/organizationActions';

import get from 'lodash.get';
import { Record } from '../../../modules/bin/utility';
import clear from '../../../components/clear';
import update from '../../../helpers/update';
import { withRouterV6 } from '../../../helpers/withRouterV6';

import Spinner from '../../../components/common/spinner';
import RecordResultMessage from '../../../components/common/recordResult';
import OrganizationTemplate from '../templateEx';

import { TemplateOptions } from '../../../components/template/templateOptions';
import { countries } from '../../../constants/countries';
import { withPageTitle } from '../../../hocs';
import { selectIsFeatureFlagExplicitlyEnabled } from '../../../modules/featureFlags/featureFlagSelectors';
import { FeatureFlagKeys } from '../../../modules/featureFlags/featureFlagTypes';
import {
  complianceComponents,
  compliancePagePath,
  isCompliancePageVisible
} from '../constants';
import { getSwiftaidUnlinkReason } from '../swiftaidConnection/unlinkWarning';
import { SwiftaidUnlinkConfirm } from '../swiftaidConnection/swiftaidUnlinkConfirm';
import { OrganizationWelcomeBanner } from './welcomeBanner/organizationWelcomeBanner';
import { isOnboardingSearch } from './onboardingQuery';

const hasJustSucceeded = (previous, current) =>
  !previous?.success?.show && current?.success?.show === true;

// Onboarding moves straight on after a save, so its result message only reports failures.
const failuresOnly = (record) =>
  record && { ...record, success: { ...record.success, show: false } };

class EditOrganization extends Component {
  constructor(props) {
    super(props);
    if (Record.notStarted(this.props.record)) {
      props.organizationActions.getOrganizationRecord(props.match.params.id);
    }

    props.generalActions.clear();

    // A save waiting on the Swiftaid disconnect warning, and why it would disconnect.
    // nextStep is the onboarding step to move to once a progress save succeeds.
    this.state = { pendingSave: null, unlinkReason: null, nextStep: null };
  }

  // The wizard is set up after rendering rather than during it: the layout reads the
  // onboarding flag, and setting it mid-render updated the layout while this page rendered.
  componentDidMount() {
    this.initTemplateIfReady();
  }

  componentDidUpdate(prevProps) {
    this.initTemplateIfReady();

    // Onboarding now lives in the template state, so the flag leaves the address bar. Done
    // here rather than at set-up, where the router would ignore a navigation on first mount.
    if (
      !prevProps.isOnboarding &&
      this.props.isOnboarding &&
      isOnboardingSearch(this.props.location.search)
    ) {
      this.props.history.replace(this.props.location.pathname);
    }

    // Onboarding moves on once a save succeeds rather than announcing it.
    if (hasJustSucceeded(prevProps.progressRecord, this.props.progressRecord)) {
      this.handleOnProgressSaved();
    }
    if (
      this.props.isOnboarding &&
      hasJustSucceeded(prevProps.updateRecord, this.props.updateRecord)
    ) {
      this.props.organizationActions.clearRecordMessages('updateRecord');
      this.props.history.push('/');
    }
  }

  // The layout hides its payment gateway prompt while onboarding, and not every page resets
  // the template on the way in, so leaving the wizard has to switch onboarding off.
  componentWillUnmount() {
    if (this.props.isOnboarding) {
      this.props.templateBuilderActions.setResultValue('isOnboarding', false);
    }
  }

  onValueChange = (path, value) => {
    this.props.templateBuilderActions.setResultValue(
      path,
      value,
      'updateRecord'
    );
  };

  // Changing or clearing the HMRC charity reference makes the API drop a Swiftaid link, so
  // such a save waits for the user to confirm it. That applies to onboarding step saves too.
  saveAfterUnlinkCheck = (record, save) => {
    const unlinkReason = getSwiftaidUnlinkReason(
      this.props.record.data.data,
      record
    );
    if (unlinkReason) {
      this.setState({ pendingSave: { record, save }, unlinkReason });
      return;
    }
    save(record);
  };

  onSave = (record) => {
    this.saveAfterUnlinkCheck(
      record,
      this.props.organizationActions.updateOrganizationRecord
    );
  };

  // Onboarding saves the organisation on every Next and moves on only once that succeeds.
  onNext = (record, nextStep) => {
    this.saveAfterUnlinkCheck(record, (confirmedRecord) => {
      this.setState({ nextStep });
      this.props.organizationActions.saveOnboardingProgress(confirmedRecord);
    });
  };

  handleOnProgressSaved = () => {
    const { nextStep } = this.state;
    this.props.organizationActions.clearRecordMessages('progressRecord');
    this.setState({ nextStep: null });
    if (nextStep) {
      this.props.history.push(
        `/organizations/edit/${this.props.match.params.id}/${nextStep}`
      );
    }
  };

  onConfirmUnlink = () => {
    const { record, save } = this.state.pendingSave;
    this.setState({ pendingSave: null, unlinkReason: null });
    save(record);
  };

  onCancelUnlink = () => {
    this.setState({ pendingSave: null, unlinkReason: null });
  };

  goToOrganisationsView = () => {
    const returnUrl = NavigationStateManager.getReturnUrl();
    this.props.history.push(returnUrl || '/organizations');
  };

  initTemplateIfReady = () => {
    if (
      Record.isRecordReady(this.props.record) &&
      !this.props.isTemplateInited
    ) {
      this.initTemplateBuilderModel();
    }
  };

  initTemplateBuilderModel = () => {
    let organization = this.props.record.data.data;

    //invertion colours
    const invertPrimaryTextColor = get(
      organization,
      'template.value.invertPrimaryTextColor'
    );
    if (!invertPrimaryTextColor) {
      organization = update.set(
        organization,
        'template.value.invertPrimaryTextColor',
        false
      );
    }
    const invertSecondaryTextColor = get(
      organization,
      'template.value.invertSecondaryTextColor'
    );
    if (!invertSecondaryTextColor) {
      organization = update.set(
        organization,
        'template.value.invertSecondaryTextColor',
        false
      );
    }
    const model = new TemplateOptions().setModel(organization);

    const defaultAmounts = get(
      organization,
      'template.value.recurringDonationSetup.defaultAmounts'
    );
    if (!defaultAmounts) {
      model.setValue('template.value.recurringDonationSetup.defaultAmounts', [
        { isDefault: true, amount: 30, description: '', image: '' },
        { isDefault: false, amount: 50, description: '', image: '' },
        { isDefault: false, amount: 75, description: '', image: '' },
        { isDefault: false, amount: 100, description: '', image: '' }
      ]);
    }

    const useAsCustomOrganization = get(
      organization,
      'template.value.customDomain.useAsCustomOrganization'
    );
    if (!useAsCustomOrganization) {
      model.hide('template.value.customDomain.domain');
    }

    const allowCRMIntegration = get(
      organization,
      'template.value.allowCRMIntegration'
    );
    if (!allowCRMIntegration) {
      model.hide('crmPlatforms');
    }

    const country = get(organization, 'address.country');
    if (!isCompliancePageVisible(country, this.props.isUkCharityEnabled)) {
      model.hide(compliancePagePath);
    }
    if (countries[country] !== countries.NZ) {
      // The alternate tax IDs and authorised signature are New Zealand only, and the
      // signature fields are required, so they have to be hidden for the United Kingdom
      // too or the compliance page could never validate.
      complianceComponents.forEach((name) => model.hide(name));
    }

    const isOnboarding = isOnboardingSearch(this.props.location.search);
    if (isOnboarding) {
      model.setValue('isOnboarding', true);
      model.hide('recurring-giving');
      // Onboarding finishes on Finance, so the CRM step, which follows it, is left out.
      model.hide('crmPlatforms');
    } else {
      model.isEdit();
    }

    // The organisation already exists here, onboarding included, so flag what it is
    // missing straight away. Only the create form starts quiet.
    model.showErrorsOnLoad();

    this.props.templateBuilderActions.setTemplateModel(model.getWithRule());
  };

  clearMessages = () => {
    this.props.organizationActions.clearRecordMessages('updateRecord');
    this.props.organizationActions.clearRecordMessages('progressRecord');
    this.props.clearStripeRecordMessages('stripeConnectRecord');
  };

  // Onboarding's Done is handled in componentDidUpdate, which returns the admin to the
  // dashboard.
  handleOnSaveSuccess = () => {
    if (this.props.isSystemAdmin) {
      this.goToOrganisationsView();
    } else {
      this.props.isOrganizationAdmin && this.goToOrganisationsView();
    }
  };

  render() {
    const isSaving = Record.isRecordLoading(this.props.updateRecord);
    const isSavingProgress = Record.isRecordLoading(this.props.progressRecord);

    if (
      !Record.isRecordReady(this.props.record) ||
      !this.props.isTemplateInited
    ) {
      return <Spinner />;
    }

    return (
      <div>
        <RecordResultMessage
          record={
            this.props.isOnboarding
              ? failuresOnly(this.props.updateRecord)
              : this.props.updateRecord
          }
          onDismiss={this.clearMessages}
          redirectOnSuccess={
            this.props.isOnboarding ? undefined : this.handleOnSaveSuccess
          }
        />

        <RecordResultMessage
          record={failuresOnly(this.props.progressRecord)}
          onDismiss={this.clearMessages}
        />

        <RecordResultMessage
          record={this.props.stripeConnectRecord}
          onDismiss={this.clearMessages}
          customSuccessMessage={I18n.t(
            'template.organisation.content.finance.stripe-success-message'
          )}
        />

        <h1>
          <Translate
            value={
              this.props.isOnboarding
                ? 'organization.organizationProfile'
                : 'organization.edit'
            }
          />
        </h1>
        <OrganizationWelcomeBanner />
        <OrganizationTemplate
          onSave={this.onSave}
          isLoading={isSaving}
          onNext={this.props.isOnboarding ? this.onNext : undefined}
          isNextLoading={isSavingProgress}
          // Editing saves with Save all; the last step's button only finishes onboarding.
          hidePublishButton={!this.props.isOnboarding}
          publishButtonLabel={
            this.props.isOnboarding ? 'general.list.done' : undefined
          }
        />

        <SwiftaidUnlinkConfirm
          reason={this.state.unlinkReason}
          onCancel={this.onCancelUnlink}
          onConfirm={this.onConfirmUnlink}
        />
      </div>
    );
  }
}

const mapState = (state) => {
  const { organization, templateBuilderEx, stripe, session } = state;
  return {
    template: organization.template,
    record: organization.record,
    updateRecord: organization.updateRecord,
    progressRecord: organization.progressRecord,
    isTemplateInited: templateBuilderEx.inited,
    stripeConnectRecord: stripe?.stripeConnectRecord,
    isOrganizationAdmin: session.isOrganizationAdmin,
    isSystemAdmin: session.isSystemAdmin,
    isOnboarding: templateBuilderEx.data.isOnboarding,
    isUkCharityEnabled: selectIsFeatureFlagExplicitlyEnabled(
      state,
      FeatureFlagKeys.UkCharity
    )
  };
};

const mapDispatch = (dispatch) => {
  return {
    templateBuilderActions: bindActionCreators(
      templateBuilderActions,
      dispatch
    ),
    organizationActions: bindActionCreators(organizationActions, dispatch),
    generalActions: bindActionCreators(generalActions, dispatch),
    clearStripeRecordMessages: bindActionCreators(clearRecordMessages, dispatch)
  };
};
/** Wrap component with page title first, then connect to Redux store */
const EditOrganizationWithPageTitle = withPageTitle((props) => {
  return props.isOnboarding ? 'Organization Profile' : 'Edit Organization';
})(EditOrganization);

const EditOrganizationContainer = clear(
  connect(mapState, mapDispatch)(EditOrganizationWithPageTitle)
);
export default withRouterV6(EditOrganizationContainer);
